/*
 * Platform environment switcher for the KB.
 *
 * Docs link to the live platform (in.hunto.ai, one.phishgrid.com, ...). This module
 * rewrites those links in the browser to the environment the reader picked, so the
 * same page works for India, EU and (for staff) dev.
 *
 * Public registry (below) ships in the bundle. Staff environments (dev hosts) are
 * NOT in the bundle: they are fetched from /internal/platform-envs.json, which sits
 * behind Cloudflare Access with the rest of /internal/*.
 */

export interface ProductDef {
  label: string
  defaultEnv: string
  envs: Record<string, string>
  aliases: string[]
}

export interface EnvOption {
  key: string
  label: string
  staff: boolean
}

interface StaffRegistry {
  labels: Record<string, string>
  products: Record<string, { envs: Record<string, string>; aliases?: string[] }>
}

interface State {
  env: string
  bases: Record<string, string>
}

const PUBLIC_LABELS: Record<string, string> = {
  in: "India (in.hunto.ai)",
  eu: "Europe (eu.hunto.ai)",
}

const PUBLIC_PRODUCTS: Record<string, ProductDef> = {
  hunto: {
    label: "Hunto",
    defaultEnv: "in",
    envs: { in: "https://in.hunto.ai", eu: "https://eu.hunto.ai" },
    aliases: ["https://in.hunto.ai", "https://eu.hunto.ai"],
  },
  phishgrid: {
    label: "Phishgrid",
    defaultEnv: "prod",
    envs: { prod: "https://one.phishgrid.com" },
    aliases: ["https://one.phishgrid.com"],
  },
  autopilot: {
    label: "Autopilot",
    defaultEnv: "prod",
    envs: { prod: "https://autopilot.hunto.ai" },
    aliases: ["https://autopilot.hunto.ai"],
  },
}

export const DEFAULT_ENV = "in"
const STORAGE_ENV = "kb.env"
const STORAGE_BASES = "kb.env.bases"
const STAFF_URL = "/internal/platform-envs.json"
const ALLOWED_SUFFIXES = [".hunto.ai", ".phishgrid.com", ".nugine.com", ".tikaj.com"]
const LOCAL_HOSTS = ["localhost", "127.0.0.1"]

// ---------- pure core (no DOM, no storage) ----------

export function validateBase(input: string): string | null {
  let u: URL
  try {
    u = new URL(input)
  } catch {
    return null
  }
  const host = u.hostname.toLowerCase()
  const local = LOCAL_HOSTS.includes(host)
  if (local ? u.protocol !== "http:" && u.protocol !== "https:" : u.protocol !== "https:") return null
  if (u.username || u.password) return null
  const ok = local || ALLOWED_SUFFIXES.some(s => host.endsWith(s))
  return ok ? u.origin : null
}

export function mergeProducts(
  base: Record<string, ProductDef>,
  staff: StaffRegistry | null
): Record<string, ProductDef> {
  const out: Record<string, ProductDef> = {}
  for (const [k, p] of Object.entries(base)) {
    out[k] = { ...p, envs: { ...p.envs }, aliases: [...p.aliases] }
  }
  if (!staff) return out
  for (const [k, sp] of Object.entries(staff.products || {})) {
    const p = out[k]
    if (!p) continue
    for (const [ek, url] of Object.entries(sp.envs || {})) {
      const origin = validateBase(url)
      if (origin) p.envs[ek] = origin
    }
    for (const a of sp.aliases || []) {
      const origin = validateBase(a)
      if (origin && !p.aliases.includes(origin)) p.aliases.push(origin)
    }
  }
  return out
}

export function originForProduct(product: ProductDef, key: string, name: string, bases: Record<string, string>): string {
  const family = key.split("-")[0]
  return bases[name] || product.envs[key] || product.envs[family] || product.envs[product.defaultEnv]
}

export function findProduct(products: Record<string, ProductDef>, origin: string): string | null {
  for (const [name, p] of Object.entries(products)) {
    if (p.aliases.includes(origin) || Object.values(p.envs).includes(origin)) return name
  }
  return null
}

export function resolveHref(href: string, products: Record<string, ProductDef>, state: State): string {
  let u: URL
  try {
    u = new URL(href)
  } catch {
    return href
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return href
  const name = findProduct(products, u.origin)
  if (!name) return href
  const target = originForProduct(products[name], state.env, name, state.bases)
  return target + u.pathname + u.search + u.hash
}

export function rewriteText(text: string, products: Record<string, ProductDef>, state: State): string {
  const origins: string[] = []
  for (const p of Object.values(products)) for (const o of [...p.aliases, ...Object.values(p.envs)]) origins.push(o)
  const uniq = [...new Set(origins)].sort((a, b) => b.length - a.length)
  if (!uniq.length) return text
  const re = new RegExp(uniq.map(o => o.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"), "g")
  return text.replace(re, m => {
    const name = findProduct(products, m)
    return name ? originForProduct(products[name], state.env, name, state.bases) : m
  })
}

// ---------- store ----------

let staffRegistry: StaffRegistry | null = null
let staffLoading: Promise<boolean> | null = null
let state: State = { env: DEFAULT_ENV, bases: {} }
let started = false
const listeners = new Set<() => void>()

const products = () => mergeProducts(PUBLIC_PRODUCTS, staffRegistry)

function labels(): Record<string, string> {
  return { ...PUBLIC_LABELS, ...(staffRegistry?.labels || {}) }
}

function knownEnv(key: string): boolean {
  return Object.values(products()).some(p => key in p.envs)
}

function notify() {
  listeners.forEach(l => l())
}

function persist() {
  try {
    if (state.env === DEFAULT_ENV) localStorage.removeItem(STORAGE_ENV)
    else localStorage.setItem(STORAGE_ENV, state.env)
    if (Object.keys(state.bases).length) localStorage.setItem(STORAGE_BASES, JSON.stringify(state.bases))
    else localStorage.removeItem(STORAGE_BASES)
  } catch {
    /* storage blocked: selection lasts for the page only */
  }
}

export function loadStaffRegistry(): Promise<boolean> {
  if (staffRegistry) return Promise.resolve(true)
  if (staffLoading) return staffLoading
  staffLoading = fetch(STAFF_URL, { credentials: "same-origin", headers: { Accept: "application/json" } })
    .then(r => (r.ok ? r.json() : null))
    .then((j: StaffRegistry | null) => {
      if (j && j.products && typeof j.products === "object") {
        staffRegistry = j
        return true
      }
      return false
    })
    .catch(() => false)
    .finally(() => {
      staffLoading = null
    })
  return staffLoading
}

export function getOptions(): EnvOption[] {
  const l = labels()
  const pub = Object.keys(PUBLIC_LABELS).map(key => ({ key, label: l[key], staff: false }))
  const staff = Object.keys(l)
    .filter(k => !(k in PUBLIC_LABELS))
    .map(key => ({ key, label: l[key], staff: true }))
  return [...pub, ...staff]
}

export function getState() {
  return { env: state.env, bases: { ...state.bases }, staffLoaded: !!staffRegistry, options: getOptions() }
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export async function setEnv(key: string): Promise<boolean> {
  if (!knownEnv(key)) {
    await loadStaffRegistry()
    if (!knownEnv(key)) {
      console.warn(`[kb] Unknown or unavailable environment "${key}". Staff environments need a staff login (Cloudflare Access).`)
      return false
    }
  }
  state = { ...state, env: key }
  persist()
  applyDom()
  notify()
  return true
}

export function setBase(product: string, url: string): boolean {
  const origin = validateBase(url)
  if (!origin || !(product in products())) {
    console.warn(`[kb] Rejected. Use kb.env.setBase("hunto" | "phishgrid" | "autopilot", "https://…") with an https URL on our own domains (or http://localhost:PORT).`)
    return false
  }
  state = { ...state, bases: { ...state.bases, [product]: origin } }
  persist()
  applyDom()
  notify()
  return true
}

export function resetEnv() {
  state = { env: DEFAULT_ENV, bases: {} }
  persist()
  applyDom()
  notify()
}

// ---------- DOM ----------

const ANCHOR_ORIG = "kbOrig"
const CODE_ORIG = "kbOrigText"

export function applyDom(root: ParentNode = document.body) {
  if (typeof document === "undefined") return
  const prods = products()
  root.querySelectorAll<HTMLAnchorElement>("a[href^='http']").forEach(a => {
    const orig = a.dataset[ANCHOR_ORIG] || a.getAttribute("href") || ""
    const next = resolveHref(orig, prods, state)
    const same = next === orig
    if (same && !a.dataset[ANCHOR_ORIG]) return
    if (a.getAttribute("href") !== next) a.setAttribute("href", next)
    if (!a.dataset[ANCHOR_ORIG]) a.dataset[ANCHOR_ORIG] = orig
    const bare = a.children.length === 0 && (a.dataset.kbOrigLabel === "1" || a.textContent?.trim() === orig)
    if (bare) {
      a.dataset.kbOrigLabel = "1"
      if (a.textContent !== next) a.textContent = next
    }
  })
  root.querySelectorAll<HTMLElement>("code").forEach(c => {
    if (c.children.length) return
    const orig = c.dataset[CODE_ORIG] ?? c.textContent ?? ""
    const next = rewriteText(orig, prods, state)
    if (next === orig && !c.dataset[CODE_ORIG]) return
    if (!c.dataset[CODE_ORIG]) c.dataset[CODE_ORIG] = orig
    if (c.textContent !== next) c.textContent = next
  })
}

// ---------- start-up + console API ----------

export async function initPlatformEnv() {
  if (typeof window === "undefined" || started) return
  started = true

  const params = new URLSearchParams(window.location.search)
  const fromUrl = params.get("env")
  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_ENV)
    const b = localStorage.getItem(STORAGE_BASES)
    if (b) {
      const parsed = JSON.parse(b) as Record<string, string>
      for (const [k, v] of Object.entries(parsed)) {
        const o = validateBase(v)
        if (o) state.bases[k] = o
      }
    }
  } catch {
    /* ignore */
  }

  const wanted = fromUrl || stored || DEFAULT_ENV
  const onInternal = window.location.pathname.startsWith("/internal/")
  if (onInternal || !(wanted in PUBLIC_LABELS)) await loadStaffRegistry()
  if (knownEnv(wanted)) state.env = wanted
  if (fromUrl && knownEnv(fromUrl)) persist()

  const api = {
    set: (key: string) => setEnv(key),
    setBase: (product: string, url: string) => setBase(product, url),
    get: () => getState(),
    list: () => getOptions().map(o => o.key),
    reset: () => resetEnv(),
  }
  ;(window as any).kb = { ...((window as any).kb || {}), env: api }

  applyDom()
  notify()

  let queued = false
  new MutationObserver(() => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => {
      queued = false
      applyDom()
    })
  }).observe(document.body, { childList: true, subtree: true })
}
